import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Clock, Crown, AlertTriangle, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export const TrialRibbon = ({ onSubscriptionUpdate }) => {
  const { user, updateUser } = useAuth();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [dismissed, setDismissed] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    // Only fetch subscription for main admin, not sub-admins
    if (user?.id && user?.role === "admin") {
      fetchSubscription();
      
      // Re-fetch on page visibility change (handles tab switching back)
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          fetchSubscription();
        }
      };
      
      // Re-fetch when local storage changes (from other components)
      const handleStorageChange = (e) => {
        if (e.key === `subscription_updated_${user.id}`) {
          fetchSubscription();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('storage', handleStorageChange);
      
      // Also poll every 30 seconds to catch updates
      const interval = setInterval(fetchSubscription, 30000);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
  }, [user?.id, user?.role]);

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/admin/${user.id}/subscription`);
      setSubscriptionInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch subscription");
    }
  };

  const handlePurchaseSubscription = async () => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/subscription/purchase?plan=${selectedPlan}`);
      toast.success(res.data.message || "Subscription activated!");
      setShowUpgrade(false);
      
      // Re-fetch subscription info to update ribbon state
      await fetchSubscription();
      
      // Trigger storage event for other components
      localStorage.setItem(`subscription_updated_${user.id}`, Date.now().toString());
      
      // Notify parent component if callback provided
      if (onSubscriptionUpdate) {
        onSubscriptionUpdate();
      }
      
      // Update user context with new subscription status
      if (updateUser) {
        updateUser({ ...user, subscription_status: "active" });
      }
    } catch (err) {
      console.error("Failed to purchase subscription", err);
      toast.error(err.response?.data?.detail || "Failed to purchase subscription");
    } finally {
      setPurchasing(false);
    }
  };

  // Don't show for sub_admin - they use parent admin's subscription
  // Don't show if not admin
  if (!user?.id || user?.role !== "admin") {
    return null;
  }

  // Don't show if dismissed or active subscription
  if (dismissed || subscriptionInfo?.subscription_status === "active") {
    return null;
  }

  // Don't show if no subscription info yet
  if (!subscriptionInfo) {
    return null;
  }

  const isTrialActive = subscriptionInfo.is_trial;
  const trialExpired = subscriptionInfo.trial_expired;
  const daysRemaining = subscriptionInfo.trial_days_remaining || 0;
  const isUrgent = daysRemaining <= 1;

  const bgColor = trialExpired 
    ? "bg-red-600" 
    : isUrgent 
    ? "bg-amber-500" 
    : "bg-blue-600";

  const plans = subscriptionInfo?.pricing ? [
    { id: "monthly", label: "Monthly", price: subscriptionInfo.pricing.monthly, period: "month" },
    { id: "quarterly", label: "Quarterly", price: subscriptionInfo.pricing.quarterly, period: "3 months", savings: "17%" },
    { id: "half_yearly", label: "Half Yearly", price: subscriptionInfo.pricing.half_yearly, period: "6 months", savings: "25%" },
    { id: "yearly", label: "Yearly", price: subscriptionInfo.pricing.yearly, period: "year", savings: "33%", recommended: true },
  ] : [];

  return (
    <>
      <div className={`${bgColor} text-white py-2 px-4 flex items-center justify-between`} data-testid="trial-ribbon">
        <div className="flex items-center gap-3">
          {trialExpired ? (
            <AlertTriangle className="w-5 h-5" />
          ) : (
            <Clock className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">
            {trialExpired ? (
              "Your trial has expired! Upgrade now to continue using the platform."
            ) : isUrgent ? (
              `Trial expires soon! Upgrade now to avoid interruption.`
            ) : (
              `Trial ends in ${daysRemaining} days`
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={() => setShowUpgrade(true)}
            className="bg-white text-gray-900 hover:bg-gray-100"
            data-testid="upgrade-now-btn"
          >
            <Crown className="w-4 h-4 mr-1" />
            Upgrade Now
          </Button>
          {!trialExpired && (
            <Button 
              size="icon" 
              variant="ghost" 
              onClick={() => setDismissed(true)}
              className="text-white hover:bg-white/20 h-6 w-6"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Upgrade Your Subscription
            </DialogTitle>
            <DialogDescription>
              Choose a plan that works best for your business
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4">
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === plan.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {plan.recommended && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    Best Value
                  </span>
                )}
                <p className="font-semibold text-sm">{plan.label}</p>
                <p className="text-2xl font-bold mt-1">₹{plan.price?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">per {plan.period}</p>
                {plan.savings && (
                  <p className="text-xs text-green-600 font-medium mt-1">Save {plan.savings}</p>
                )}
              </button>
            ))}
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">What's included:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Unlimited drivers and customers</li>
              <li>• Real-time ride tracking</li>
              <li>• Commission management</li>
              <li>• City-based pricing</li>
              <li>• Sub-admin accounts</li>
              <li>• Priority support</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgrade(false)}>
              Maybe Later
            </Button>
            <Button onClick={handlePurchaseSubscription} disabled={purchasing}>
              {purchasing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Proceed to Payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrialRibbon;
